import React, { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Alert, Box, Button, Card, CardContent, Chip, CircularProgress, Dialog,
  DialogActions, DialogContent, DialogTitle, Divider, Grid, MenuItem, Select, Snackbar,
  Stack, TextField, Typography
} from '@mui/material';
import { CheckCircle, Block, Refresh, Visibility } from '@mui/icons-material';
import AdminHeader from '../../components/admin/adminHeader.tsx';
import Slidemenu from '../../components/admin/adminSlidemenu.tsx';

const API = process.env.REACT_APP_SERVER_ENDPOINT || '';
const AUTH_KEY = '7b4e1fd03f57885c1ff900ad76ea3810bb7160c7157a4c31cf2de3';

// Vendor-submitted descriptions come from a rich-text editor and are stored
// as raw HTML (e.g. "<p>test product</p>"). Shown as plain text inside a
// MUI Typography, the tags render literally instead of being interpreted —
// hence the "<p>test productr</p>" showing up as-is. Strip the markup down
// to plain readable text for this admin-only detail view.
const stripHtml = (html?: string) => {
  if (!html) return '';
  return String(html)
    .replace(/<\/(p|div|li|br|h[1-6])>/gi, '\n')
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<[^>]+>/g, '')
    .replace(/&nbsp;/gi, ' ')
    .replace(/&amp;/gi, '&')
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>')
    .replace(/\n{2,}/g, '\n')
    .trim();
};

// Grouping the detail grid into labelled sections (rather than one long
// unbroken list of 20+ fields) is what makes it read as "neat" instead of
// a flat dump.
const PRODUCT_DETAIL_SECTIONS = (productDetail: any) => {
  // Discount = Price - Discounted (retail) price. Never negative: if the
  // discounted price is equal to or higher than the price, there's no discount.
  const currency = productDetail.currency || 'AED';
  const price = Number(productDetail.product_price || 0);
  const discountedPrice = Number(productDetail.product_retail_price || 0);
  const discountAmount = Math.max(price - discountedPrice, 0);

  return [
  {
    title: 'Vendor & Category',
    fields: [
      ['Product Number', productDetail.product_number || productDetail.id],
      ['Vendor ID', productDetail.vendor_id ?? productDetail.seller_id],
      ['Vendor Name', productDetail.vendor_name || productDetail.business_name],
      ['Category', productDetail.cat_name || productDetail.cat_id],
      ['Sub Category', productDetail.sub_cat_name || productDetail.sub_cat_id],
    ],
  },
  {
    title: 'Status',
    fields: [
      ['Approval Status', productDetail.approval_status],
      ['Product Status', productDetail.status === 1 ? 'Active' : productDetail.status === 0 ? 'Inactive' : productDetail.status],
      ['Availability', productDetail.availability],
      ['COD', Number(productDetail.cod_available ?? 0) === 1 ? 'Enabled' : 'Disabled'],
    ],
  },
  {
    title: 'Pricing & Stock',
    fields: [
      ['Currency', productDetail.currency],
      ['Price', `${productDetail.currency || 'AED'} ${Number(productDetail.product_price || 0).toFixed(2)}`],
      ['Discounted Price', `${currency} ${discountedPrice.toFixed(2)}`],
      ['Discount', `${currency} ${discountAmount.toFixed(2)}`],
      ['Stock', productDetail.product_stock ?? productDetail.stock_quantity ?? 0],
      ['Product Unit', productDetail.product_unit],
      ['Product Quantity', productDetail.product_quantity],
      ['Max Order Quantity', productDetail.max_order_quantity],
    ],
  },
  {
    title: 'Logistics',
    fields: [
      ['Tax', `${productDetail.tax1_name || 'VAT'} ${productDetail.tax1_per ?? 0}%`],
      ['Shipping Charge', productDetail.shipping_charge],
      ['Delivery Model', productDetail.delivery_model],
      ['Warehouse', productDetail.warehouse_name || productDetail.warehouse_id],
    ],
  },
  {
    title: 'Timestamps',
    fields: [
      ['Created', productDetail.createddt],
      ['Updated', productDetail.updatedt],
    ],
  },
  ];
};

type Product = {
  product_id: number; product_number?: number; product_name: string; product_main_image?: string;
  product_price?: number; product_retail_price?: number; product_stock?: number;
  approval_status: 'pending'|'approved'|'rejected'|'blocked'; approval_notes?: string;
  vendor_id?: number; vendor_name?: string; business_name?: string;
  vendor_email?: string; vendor_contact?: string;
};

const request = async (url: string, body?: any) => {
  const token = localStorage.getItem('admin_token');
  const response = await fetch(`${API}${url}`, {
    method: body ? 'POST' : 'GET',
    headers: {
      Authorization: token ? `Bearer ${token}` : '',
      authkey: AUTH_KEY,
      ...(body ? {'Content-Type': 'application/json'} : {})
    },
    body: body ? JSON.stringify(body) : undefined
  });
  const data = await response.json();
  if (!response.ok || data.error) throw new Error(data.message || 'Request failed');
  return data;
};

export default function VendorProductApprovals() {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [status, setStatus] = useState(() => localStorage.getItem('tab_vendor_product_approval') || 'pending');
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(false);
  const [dialog, setDialog] = useState<{open:boolean; action:'approved'|'rejected'|'blocked'; product?:Product}>({open:false,action:'approved'});
  const [notes, setNotes] = useState('');
  const [snack, setSnack] = useState({open:false,message:'',severity:'success' as 'success'|'error'});
  // Final "are you sure?" step shown before the approve/reject/block call is
  // actually sent, so a misclick in the notes dialog can still be caught.
  const [confirmOpen, setConfirmOpen] = useState(false);

  // Full product details shown to the admin before they approve/reject —
  // the row card only has a summary, so this fetches everything else
  // (description, category, discount, additional images, etc.) on demand.
  const [detailProductId, setDetailProductId] = useState<number | null>(null);

  // Arriving here from a notification click hands us a specific product_id —
  // open that product's own detail dialog instead of just the full list.
  const location = useLocation();
  const navigate = useNavigate();
  const openProductId = (location.state as any)?.openProductId;

  useEffect(() => {
    if (openProductId != null && status !== 'all') {
      setStatus('all');
    }
  }, [openProductId, status]);

  useEffect(() => {
    if (openProductId == null || products.length === 0) return;
    const match = products.find((p) => Number(p.product_id) === Number(openProductId));
    if (match) {
      setDetailProductId(match.product_id);
      // Clear the nav state so revisiting this page doesn't keep reopening it.
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [openProductId, products, navigate, location.pathname]);
  // FIX: this used to call useGetProductDetailQuery -> /product/find, the
  // customer-storefront lookup that only returns LIVE (approved) products.
  // A pending/rejected/blocked vendor product isn't live yet — that's
  // exactly the state an admin needs to review here — so that call 404'd
  // for the products this dialog exists to show, and every field silently
  // fell back to N/A. Admin review now goes through a dedicated,
  // admin-scoped endpoint (/admin/vendor-products/:id/detail) that has no
  // live/approved gate.
  const [productDetail, setProductDetail] = useState<any>({});
  const [productDetailImages, setProductDetailImages] = useState<any[]>([]);
  const [isDetailLoading, setIsDetailLoading] = useState(false);

  useEffect(() => {
    if (!detailProductId) {
      setProductDetail({});
      setProductDetailImages([]);
      return;
    }
    let cancelled = false;
    (async () => {
      setIsDetailLoading(true);
      try {
        const res = await request(`/admin/vendor-products/${detailProductId}/detail`);
        if (cancelled) return;
        setProductDetail(res.data || {});
        setProductDetailImages(res.images || []);
      } catch (e: any) {
        if (cancelled) return;
        setProductDetail({});
        setProductDetailImages([]);
        setSnack({ open: true, message: e.message || 'Unable to load product details', severity: 'error' });
      } finally {
        if (!cancelled) setIsDetailLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [detailProductId]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await request(`/admin/vendor-products?status=${encodeURIComponent(status)}`);
      setProducts(data.data || []);
    } catch (e:any) {
      setSnack({open:true,message:e.message || 'Unable to load vendor products',severity:'error'});
    } finally { setLoading(false); }
  }, [status]);

  useEffect(() => { localStorage.setItem('tab_vendor_product_approval', status); load(); }, [status, load]);

  const actionLabel = (action: 'approved'|'rejected'|'blocked') =>
    action==='approved' ? 'approve' : action==='rejected' ? 'reject' : 'block';

  // Notes dialog's own button no longer calls the API directly — it just
  // opens the final confirm popup. The actual API call happens in confirmDecide.
  const askConfirm = () => {
    if (dialog.action === 'rejected' && !notes.trim()) return;
    setConfirmOpen(true);
  };

  const confirmDecide = async () => {
    if (!dialog.product) return;
    try {
      await request(`/admin/vendor-products/${dialog.action === 'approved' ? 'approve' : dialog.action === 'rejected' ? 'reject' : 'block'}`, {
        product_id: dialog.product.product_id, notes
      });
      setSnack({
        open:true,
        message: dialog.action === 'approved' ? 'Product approved and published.' : dialog.action === 'rejected' ? 'Product rejected.' : 'Product blocked and unpublished.',
        severity:'success'
      });
      setConfirmOpen(false); setDialog({open:false,action:'approved'}); setNotes(''); load();
    } catch (e:any) {
      setConfirmOpen(false);
      setSnack({open:true,message:e.message || 'Unable to update product',severity:'error'});
    }
  };

  // Shared compact sizing for every popup on this page — roughly half the
  // screen width instead of the old near-full-width dialogs.
  const popupPaperProps = { sx: { width: { xs: '92%', sm: '55%', md: '50%' }, maxWidth: '640px' } };

  return (
    <Box sx={{display:'flex',minHeight:'100vh',bgcolor:'#f5f6f7'}}>
      <Slidemenu onToggle={() => setDrawerOpen(!drawerOpen)} mobileOpen={drawerOpen} />
      <Box sx={{flex:1,minWidth:0}}>
        <AdminHeader onToggleMenu={() => setDrawerOpen(!drawerOpen)} />
        <Box sx={{p:{xs:2,md:3},maxWidth:1500,mx:'auto'}}>
          <Box sx={{display:'flex',justifyContent:'space-between',alignItems:'center',mb:2,flexWrap:'wrap',gap:1}}>
            <Box>
              <Typography variant="h5" fontWeight={700}>Vendor Product Approval</Typography>
              <Typography variant="body2" color="text.secondary">
                Vendor products stay hidden until an admin approves them. Approved products can be blocked later.
              </Typography>
            </Box>
            <Stack direction="row" spacing={1}>
              <Select size="small" value={status} onChange={e=>setStatus(e.target.value)} sx={{minWidth:130}}>
                <MenuItem value="pending">Pending</MenuItem>
                <MenuItem value="approved">Approved</MenuItem>
                <MenuItem value="rejected">Rejected</MenuItem><MenuItem value="blocked">Blocked</MenuItem>
                <MenuItem value="all">All</MenuItem>
              </Select>
              <Button variant="outlined" startIcon={<Refresh/>} onClick={load}>Refresh</Button>
            </Stack>
          </Box>

          <Card sx={{borderRadius:2}}>
            <CardContent>
              {loading ? <Box sx={{py:8,textAlign:'center'}}><CircularProgress/></Box> :
               products.length === 0 ? <Alert severity="info">No {status} vendor products.</Alert> :
               <Stack spacing={1.5}>
                {products.map(p => (
                  <Box key={p.product_id} sx={{p:2,border:'1px solid #e3e5e8',borderRadius:2,display:'flex',gap:2,alignItems:'center',flexWrap:'wrap'}}>
                    <Box sx={{width:72,height:72,borderRadius:1,bgcolor:'#f0f1f2',overflow:'hidden',flexShrink:0}}>
                      {p.product_main_image && <img src={`${API}/images/${p.product_main_image}`} alt="" style={{width:'100%',height:'100%',objectFit:'cover'}} />}
                    </Box>
                    <Box sx={{flex:1,minWidth:220}}>
                      <Typography fontWeight={700}>{p.product_name}</Typography>
                      <Typography variant="caption" color="text.secondary">Product No: {p.product_number || p.product_id} • Vendor ID: {p.vendor_id || 'N/A'}</Typography>
                      <Typography variant="body2" color="text.secondary">{p.business_name || p.vendor_name || `Vendor #${p.vendor_id}`}</Typography>
                      <Typography variant="caption" color="text.secondary">{p.vendor_email || ''} {p.vendor_contact ? `• ${p.vendor_contact}` : ''}</Typography>
                    </Box>
                    <Box sx={{minWidth:140}}>
                      <Typography variant="body2">Stock: {Number(p.product_stock || 0)}</Typography>
                      <Typography variant="body2">Price: AED {Number(p.product_retail_price || p.product_price || 0).toFixed(2)}</Typography>
                      <Chip size="small" label={p.approval_status} color={p.approval_status==='approved'?'success':p.approval_status==='blocked'?'error':'warning'} />
                    </Box>
                    <Stack direction="row" spacing={1}>
                      <Button size="small" variant="outlined" startIcon={<Visibility/>}
                        onClick={()=>setDetailProductId(p.product_id)}>View Details</Button>
                      {p.approval_status !== 'approved' &&
                        <Button size="small" variant="contained" color="success" startIcon={<CheckCircle/>}
                          onClick={()=>{setNotes('');setDialog({open:true,action:'approved',product:p})}}>Approve</Button>}
                      {p.approval_status !== 'approved' && p.approval_status !== 'rejected' &&
                        <Button size="small" variant="outlined" color="error" onClick={()=>{setNotes('');setDialog({open:true,action:'rejected',product:p})}}>Reject</Button>}
                      {p.approval_status !== 'blocked' &&
                        <Button size="small" variant="outlined" color="error" startIcon={<Block/>}
                          onClick={()=>{setNotes('');setDialog({open:true,action:'blocked',product:p})}}>Block</Button>}
                    </Stack>
                  </Box>
                ))}
               </Stack>}
            </CardContent>
          </Card>
        </Box>
      </Box>

      <Dialog open={dialog.open} onClose={()=>setDialog({open:false,action:'approved'})} PaperProps={popupPaperProps}>
        <DialogTitle>{dialog.action==='approved' ? 'Approve Product' : dialog.action==='rejected' ? 'Reject Product' : 'Block Product'}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{mb:2}}>
            {dialog.product?.product_name} — {dialog.action==='approved' ? 'this will publish the product.' : 'this will hide the product until corrected.'}
          </Typography>
          <TextField fullWidth multiline minRows={3} label={dialog.action==='rejected' ? 'Rejection reason *' : 'Admin note (optional)'} value={notes} onChange={e=>setNotes(e.target.value)} error={dialog.action==='rejected' && !notes.trim()} helperText={dialog.action==='rejected' && !notes.trim() ? 'Rejection reason is mandatory.' : ''} />
        </DialogContent>
        <DialogActions>
          <Button onClick={()=>setDialog({open:false,action:'approved'})}>Cancel</Button>
          <Button variant="contained" color={dialog.action==='approved'?'success':'error'} onClick={askConfirm} disabled={dialog.action==='rejected' && !notes.trim()}>
            {dialog.action==='approved'?'Approve & Publish':dialog.action==='rejected'?'Reject Product':'Block & Unpublish'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Final confirmation popup — a deliberate second step before the
          approve/reject/block call actually goes out. */}
      <Dialog open={confirmOpen} onClose={()=>setConfirmOpen(false)} PaperProps={popupPaperProps}>
        <DialogTitle>Confirm {actionLabel(dialog.action)}</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Are you sure you want to {actionLabel(dialog.action)} "{dialog.product?.product_name}"?
            {dialog.action==='rejected' && ' The vendor will see your rejection reason.'}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={()=>setConfirmOpen(false)}>Cancel</Button>
          <Button variant="contained" color={dialog.action==='approved'?'success':'error'} onClick={confirmDecide}>
            Yes, {actionLabel(dialog.action)}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snack.open} autoHideDuration={4000} onClose={()=>setSnack({...snack,open:false})}
        message={snack.message} />

      {/* Product Details dialog — shown before an admin approves/rejects/blocks */}
      <Dialog open={!!detailProductId} onClose={()=>setDetailProductId(null)} PaperProps={popupPaperProps}>
        <DialogTitle>Product Details</DialogTitle>
        <DialogContent dividers>
          {isDetailLoading ? (
            <Box sx={{py:6,textAlign:'center'}}><CircularProgress/></Box>
          ) : (
            <Stack spacing={2}>
              <Box sx={{display:'flex',gap:2.5,alignItems:'center',flexWrap:'wrap'}}>
                <Box sx={{width:96,height:96,borderRadius:1,bgcolor:'#f0f1f2',overflow:'hidden',flexShrink:0}}>
                  {productDetail.product_main_image &&
                    <img src={`${API}/images/${productDetail.product_main_image}`} alt="" style={{width:'100%',height:'100%',objectFit:'cover'}} />}
                </Box>
                <Box sx={{flex:1,minWidth:180,pl:0.5}}>
                  <Typography variant="h6" fontWeight={700}>{productDetail.prod_name || productDetail.product_name}</Typography>
                  <Typography variant="body2" color="text.secondary">{productDetail.prod_short_name}</Typography>
                </Box>
              </Box>

              {productDetail.prod_desc && (
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">Description</Typography>
                  <Typography variant="body2" sx={{whiteSpace:'pre-line'}}>{stripHtml(productDetail.prod_desc)}</Typography>
                </Box>
              )}

              <Divider />

              <Stack spacing={2}>
                {PRODUCT_DETAIL_SECTIONS(productDetail).map(section => (
                  <Box key={section.title} sx={{border:'1px solid #eceef0',borderRadius:1.5,p:1.75,bgcolor:'#fafbfc'}}>
                    <Typography variant="subtitle2" sx={{mb:1,fontWeight:700,color:'#556'}}>{section.title}</Typography>
                    <Grid container spacing={1.5}>
                      {section.fields.map(([label,value]) => (
                        <Grid item xs={6} sm={4} key={String(label)}>
                          <Typography variant="caption" color="text.secondary" sx={{display:'block'}}>{label}</Typography>
                          <Typography variant="body2" fontWeight={600} sx={{wordBreak:'break-word'}}>
                            {value === undefined || value === null || value === '' ? '-' : String(value)}
                          </Typography>
                        </Grid>
                      ))}
                    </Grid>
                  </Box>
                ))}
              </Stack>

              {productDetailImages.length > 0 && (
                <Box>
                  <Typography variant="subtitle2" color="text.secondary" sx={{mb:1}}>Additional Images</Typography>
                  <Stack direction="row" spacing={1} flexWrap="wrap">
                    {productDetailImages.map((img: any, i: number) => (
                      <Box key={img.id || i} sx={{width:64,height:64,borderRadius:1,overflow:'hidden',bgcolor:'#f0f1f2'}}>
                        <img src={`${API}/images/${img.product_image || img.image}`} alt="" style={{width:'100%',height:'100%',objectFit:'cover'}} />
                      </Box>
                    ))}
                  </Stack>
                </Box>
              )}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={()=>setDetailProductId(null)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}